import { sum } from 'lodash';
import { createSelector } from 'reselect';
import { COLLEGE_PLAN_TYPES } from '../../../settings/settings';
import {
  getEligibleAgreements,
  hasEligibilitySucceed,
} from '../../../redux/modules/eligibleAgreements/eligibleAgreements.selectors';

/**
 * @param {Object} state
 * @returns {Object.<string, boolean>}
 */
export const getProductTypes = (state) => state.userProductTypes?.entity || {};

export const getFundsByAgreementID = (state) =>
  state.userFunds.fundsByAgreementID;

export const getHasAPP = createSelector(getProductTypes, (productTypes) => {
  const { hasAPP } = productTypes || {};
  return hasAPP;
});

/**
 * @param {Object} state
 * @returns {boolean}
 */
export const getHasRAS = createSelector(
  getProductTypes,
  /**
   * @param {Object.<string, boolean>} productTypes
   * @returns {boolean}
   */
  (productTypes) => {
    const { hasRAS } = productTypes || {};
    return hasRAS;
  }
);

/**
 * @param {Object} state
 * @returns {boolean}
 */
export const getHasAnyAdvisoryAccounts = createSelector(
  getProductTypes,
  /**
   * @param {Object.<string, boolean>} productTypes
   * @returns {boolean}
   */
  (productTypes) => {
    const { hasRAS, hasAPP } = productTypes || {};
    return hasRAS || hasAPP;
  }
);

/**
 * @param {Object} fundsByAgreementID
 * @returns {boolean}
 */
export const getHasAPPOnly = createSelector(
  getFundsByAgreementID,
  (agreementsObject) => {
    return (
      Object.values(agreementsObject || {}).filter(
        (fund) => fund.productType !== 'ManagedAccount'
      ).length === 0
    );
  }
);

/**
 * @param {Object.<string, boolean>} productTypes
 * @returns {boolean}
 */
export const getHasCollegeOnly = createSelector(
  getProductTypes,
  ({
    hasAK529,
    hasMD529,
    hasNAT529,
    hasAPP,
    hasRAS,
    hasBrokerage,
    hasMutualFund,
  }) => {
    return (
      (hasAK529 || hasMD529 || hasNAT529) &&
      !(hasAPP || hasRAS || hasBrokerage || hasMutualFund)
    );
  }
);

/**
 * @param {Object.<string, boolean>} productTypes
 * @returns {boolean}
 */
export const getHasInheritedIRAAgreementsOnly = createSelector(
  getProductTypes,
  ({ hasInheritedIRAAgreementsOnly }) => {
    return hasInheritedIRAAgreementsOnly;
  }
);

/**
 * @param {Object.<string, boolean>} productTypes
 * @returns {boolean}
 */
export const getHasStopFeatureOnAnyAgreement = createSelector(
  getProductTypes,
  ({ hasStopFeatureOnAnyAgreement }) => {
    return hasStopFeatureOnAnyAgreement;
  }
);

/**
 * @param {Object.<string, boolean>} productTypes
 * @returns {boolean}
 */
export const getIs529CustodianOnly = createSelector(
  getProductTypes,
  ({ is529CustodianOnly }) => {
    return is529CustodianOnly;
  }
);

/**
 * @param {Object.<string, boolean>} productTypes
 * @returns {boolean}
 */
export const getRPSOrTRAConly = createSelector(
  getProductTypes,
  ({ rpsonly, tracOnly }) => {
    return rpsonly || tracOnly;
  }
);

/**
 * @param {Object} state
 * @returns {number}
 */
export const getCollegeTypeCount = createSelector(
  getProductTypes,
  /**
   * @param {Object.<string, boolean>} productTypes
   * @returns {number}
   */
  ({ hasAK529, hasMD529, hasNAT529 }) =>
    sum([hasAK529, hasMD529, hasNAT529]) || 0
);

/**
 * @param {Object} state
 * @returns {boolean}
 */
export const getMD529Decom = createSelector(
  getCollegeTypeCount,
  getEligibleAgreements,
  hasEligibilitySucceed,
  /**
   * @param {number} collegeTypeCount
   * @param {number} eligibleAgreements
   * @returns {boolean}
   */
  (collegeTypeCount, eligibleAgreements, eligibilitySucceed) =>
    collegeTypeCount === 0 &&
    eligibleAgreements.length === 0 &&
    eligibilitySucceed
);

/**
 * @param {Object} state
 * @returns {boolean}
 */
export const getHasMD529Only = createSelector(
  getProductTypes,
  ({ hasAK529, hasMD529, hasNAT529 }) => {
    return hasMD529 && !(hasAK529 || hasNAT529);
  }
);

export const getHasMD529 = createSelector(
  getProductTypes,
  ({ hasMD529 }) => hasMD529
);

/**
 * @param {Object} state
 * @returns {boolean}
 */
export const getHasAK529Only = createSelector(
  getProductTypes,
  ({ hasAK529, hasMD529, hasNAT529 }) => {
    return hasAK529 && !(hasMD529 || hasNAT529);
  }
);

/**
 * @param {Object} state
 * @returns {boolean}
 */
export const getHasNAT529Only = createSelector(
  getProductTypes,
  ({ hasAK529, hasMD529, hasNAT529 }) => {
    return hasNAT529 && !(hasAK529 || hasMD529);
  }
);

/**
 * @param {Object} state
 * @returns {boolean}
 */
export const getHasMixedCollegeTypes = createSelector(
  getCollegeTypeCount,
  /**
   * @param {number} collegeTypeCount
   * @returns {boolean}
   */
  (collegeTypeCount) => collegeTypeCount > 1
);

/**
 * @param {Object} state
 * @returns {('MD'|'NAT'|'AK')}
 */
export const getOnlyCollegeType = createSelector(
  getProductTypes,
  getCollegeTypeCount,
  /**
   * @param {Object.<string, boolean>} productTypes
   * @param {number} collegeTypeCount
   * @returns {('MD'|'NAT'|'AK')|null} - The entity object from which this works, which comes from the server, has them labeled as MD529, NAT529, AK529.
   */
  (productTypes, collegeTypeCount) =>
    collegeTypeCount === 0
      ? null
      : Object.keys(productTypes)
          .filter((key) => /529/.test(key) && productTypes[key])[0]
          .replace(/has([a-zA-Z]+)529/, '$1')
);

/**
 * @param {Object} state
 * @returns {String}
 */
export const getCollegePlanType = createSelector(
  /**
   * @param {number} collegeTypeCount
   * @param {Object.<string, boolean>} productTypes
   * @returns {String}
   */
  getCollegeTypeCount,
  getProductTypes,
  (_collegeTypeCount, { hasMD529, hasNAT529 }) =>
    hasNAT529
      ? COLLEGE_PLAN_TYPES.NATIONAL_529
      : hasMD529
      ? COLLEGE_PLAN_TYPES.MARYLAND_529
      : COLLEGE_PLAN_TYPES.ALASKA_529
);
